import { pick } from 'lodash';

import { formatCurrency, parseNumber } from 'modules/common/utils';

const variantsKey = [
  'id',
  'uuid',
  'price',
  'price_human',
  'remaining',
  'discounted_price',
  'discounted_price_human',
  'final_price',
  'final_price_human',
  'product_variant_uuid',
  'code',
  'name',
  'has_variant',
  'photo_url',
  'sku',
];
const transformVariants = (data) => {
  const { code, price, discounted_price } = data?.attributes || data || {};
  const final_price = discounted_price || price;

  const args = {
    ...data,
    ...data?.attributes,
    id: +data?.id,
    name: code,
    price_human: formatCurrency(parseNumber(price || 0)),
    has_price: price !== '0.00',
    discounted_price_human: formatCurrency(parseNumber(discounted_price || 0)),
    has_variant: false,
    final_price: parseNumber(final_price),
    final_price_human: formatCurrency(parseNumber(final_price)),
    price: parseNumber(price) || 0,
    discounted_price: parseNumber(discounted_price) || 0,
  };

  return pick(args, variantsKey);
};

const transformVariantsRange = (data) => {
  const variantsPrice = data
    ?.filter((item) => item?.price)
    ?.map((item) => item?.price);
  const variantsDiscount = data
    ?.filter((item) => item?.discounted_price)
    ?.map((item) => item?.discounted_price);

  const variantsMarketPrice = data
    ?.filter((item) => item?.discounted_price || item?.price)
    ?.map((item) => item?.discounted_price || item?.price);

  const formatMinMax = (arr) => {
    if (!arr?.length) return '';

    const min = Math.min(...arr);
    const max = Math.max(...arr);

    return {
      min,
      min_human: formatCurrency(min),
      max,
      max_human: formatCurrency(max),
    };
  };

  const args = {
    variants: data,
    srp: formatMinMax(variantsPrice?.map((item) => parseNumber(item))),
    dsp: formatMinMax(variantsDiscount?.map((item) => parseNumber(item))),
    market_price: formatMinMax(
      variantsMarketPrice?.map((item) => parseNumber(item))
    ),
  };

  return args;
};

export const transformProductAddonsOptions = (data) => {
  const keys = [
    'addon_group_uuid',
    'created_at',
    'name',
    'photo_url',
    'price',
    'price_human',
    'updated_at',
    'uuid',
  ];

  const { price } = data?.attributes || {};

  const args = {
    ...data,
    ...data?.attributes,
    price_human: formatCurrency(parseNumber(price)),
    id: data?.id,
  };

  return pick(args, keys);
};

export const transformProductAddons = (data) => {
  const keys = [
    'created_at',
    'is_active',
    'limit',
    'name',
    'addons',
    'product_uuid',
    'updated_at',
    'uuid',
  ];

  const { addons } = data || {};

  const args = {
    ...data,
    ...data?.attributes,
    addons: (addons || [])?.map(transformProductAddonsOptions),
    id: data?.id,
  };

  return pick(args, keys);
};

export const transformGetProducts = (data, included) => {
  const variantsIncluded = (included || [])?.filter(
    (item) =>
      `${item?.type}` === 'activeVariantsWithInventory' &&
      `${item?.attributes?.product_uuid}` === `${data?.attributes?.uuid}`
  );

  const addons = (included || [])?.filter(
    (item) => `${item?.type}` === 'product_addons'
  );
  const addonsIncluded = (included || [])
    ?.filter(
      (item) =>
        `${item?.type}` === 'product_addon_group' &&
        `${item?.attributes?.product_uuid}` === `${data?.attributes?.uuid}`
    )
    ?.map((item) => ({
      ...item,
      addons:
        addons?.filter(
          (subItem) =>
            subItem?.attributes?.addon_group_uuid === item?.attributes?.uuid
        ) || [],
    }));

  const { price, discounted_price } = data?.attributes || {};
  const final_price = discounted_price || price;

  const keys = [
    'description',
    'discounted_price',
    'has_variant',
    'is_available',
    'low_count',
    'name',
    'original_description',
    'photo_url',
    'price',
    'remaining',
    'sku',
    'type',
    'uuid',
    'vendor_inventory_uuid',
    'variants',
    'final_price',
    'final_price_human',
    'addons',
    'srp',
    'dsp',
    'market_price',
    'variants_stocks',
  ];

  const formattedVariants = transformVariantsRange(
    variantsIncluded?.map(transformVariants)
  );
  const variantsStocksTotal = formattedVariants?.variants?.reduce(
    (acc, item) => acc + item?.remaining,
    0
  );

  const args = {
    ...data,
    ...data?.attributes,
    ...formattedVariants,
    final_price: parseNumber(final_price),
    variants_stocks: variantsStocksTotal,
    final_price_human: formatCurrency(parseNumber(final_price)),
    addons: addonsIncluded?.map(transformProductAddons),
    id: data?.id,
  };

  return pick(args, keys);
};

export const transformGetCategories = (data) => {
  const keys = ['id', 'category_uuid', 'title'];

  const { custom_elements } = data?.attributes || {};

  const args = {
    ...data,
    ...data?.attributes,
    title: custom_elements?.title,
    id: data?.id,
  };

  return pick(args, keys);
};
